// DEPENDENCIES ---------------------------------------------------------------- //

import { Moves } from '@gql/base-types';
import dayjs from 'dayjs';
import { formatRelativeTime } from '@features/moves/components/FlowVisualization';
import { hasPickupFlow, hasDeliveryFlow } from '@features/moves/utils/checkConfirmationRequirement';

// TYPES ---------------------------------------------------------------- //

export interface ConfirmationAttempt {
  id: number;
  attemptNumber: number;
  status: string; // Raw database status (e.g., "pending", "sent", "started", "ready", "not ready", "processing")
  sentAt: string;
  timeAgo: string;
  triggerTime?: string; // ISO timestamp for flow visualization
  output?: string;
  magicUrl?: string;
  escalated?: boolean; // Whether this attempt is escalated
  expirationTime?: string; // When request expires (for determining if expired)
}

export interface LocationConfirmationData {
  name: string;
  address: string;
  region: string | null;
  contact: {
    name: string;
    email: string | null;
    phone: string | null;
  } | null;
  confirmation: {
    status: string; // Raw database status from latest attempt
    magicUrl: string | null;
    attempts: ConfirmationAttempt[];
  } | null;
  confirmationset: {
    id: number;
    confirmation_flow?: unknown;
    pickup_workflow_id?: number | null;
    delivery_workflow_id?: number | null;
  } | null;
  relevantRequests: any[];
}

// UTILITY ---------------------------------------------------------------- //

/**
 * Gets confirmation data for a specific location (pickup or delivery)
 */
export const getLocationConfirmation = (
  move: Moves | null | undefined,
  type: 'pickup' | 'delivery'
): LocationConfirmationData | null => {
  if (!move) return null;

  // Get location info
  const location = move.lane?.[type];
  if (!location) return null;

  const name = location.name || '';
  const address = location.address || '';
  const region = location.region?.name || null;

  // Get contacts from contactstomoves if no confirmation data
  const getContactFromContactstomoves = () => {
    const contacts = move.contactstomoves || [];
    const typeContact = contacts.find((ctm: any) => ctm.contact_type === type);
    if (!typeContact?.contact) return null;

    return {
      name: typeContact.contact.name || '',
      email: typeContact.contact.email || null,
      phone: typeContact.contact.mobile_number || typeContact.contact.landline_number || null,
    };
  };

  // Get scenario and confirmation requests
  const scenarioToMove = move.scenariotomoves?.[0];
  if (!scenarioToMove?.scenario) {
    // No scenario means no confirmation data, but we can still get contacts
    const contact = getContactFromContactstomoves();
    return {
      name,
      address,
      region,
      contact,
      confirmation: null,
      confirmationset: null,
      relevantRequests: [],
    };
  }

  const scenario = scenarioToMove.scenario;
  const confirmationRequests = scenario.confirmationrequests || [];
  const confirmationset = confirmationRequests[0]?.confirmationset || null;

  // Determine if this is pickup or delivery based on workflow_id
  const isPickup = type === 'pickup';
  const relevantRequests = confirmationRequests.filter((req: any) => {
    if (!req.workflow?.id || !confirmationset) return false;

    // Check if workflow_id matches pickup_workflow_id or delivery_workflow_id
    if (isPickup && confirmationset.pickup_workflow_id === req.workflow.id) {
      return true;
    }
    if (!isPickup && confirmationset.delivery_workflow_id === req.workflow.id) {
      return true;
    }

    // Fallback: check workflow name/label
    const workflowName = req.workflow?.name?.toLowerCase() || '';
    const workflowLabel = req.workflow?.label?.toLowerCase() || '';
    const hasPickupKeyword = workflowName.includes('pickup') || workflowLabel.includes('pickup');

    return isPickup === hasPickupKeyword;
  });

  // Get contact from the most recent request, or fallback to contactstomoves
  const latestRequest = relevantRequests[0];
  let contact = latestRequest?.contact
    ? {
        name: latestRequest.contact.name || '',
        email: latestRequest.contact.email || null,
        phone: latestRequest.contact.mobile_number || latestRequest.contact.landline_number || null,
      }
    : null;

  // Fallback to contactstomoves if no contact from confirmation requests
  if (!contact) {
    contact = getContactFromContactstomoves();
  }

  // Build confirmation attempts - use raw database status directly
  const attempts: ConfirmationAttempt[] = relevantRequests.map((req: any, index: number) => {
    const triggerTime = req.trigger_time ? dayjs(req.trigger_time) : null;
    // Use formatRelativeTime for consistent time calculation across the app
    const timeAgo = triggerTime && req.trigger_time ? formatRelativeTime(req.trigger_time) : '';
    const triggerTimeISO = req.trigger_time || undefined;

    // Magic URL is a direct column on confirmationrequests table
    const magicUrl: string | null = req.magic_url || null;

    // Use raw database status directly - no mapping needed
    const status: string = req.status || 'pending';

    // Determine if this attempt is escalated
    // Escalated can be true from the flag OR if status is "not ready"
    const isEscalated = req.escalated || req.status === 'not ready';

    return {
      id: req.id,
      attemptNumber: relevantRequests.length - index, // Most recent is attempt 1
      status,
      sentAt: triggerTime ? triggerTime.format('MMM D, YYYY [at] h:mm A') : '',
      timeAgo,
      triggerTime: triggerTimeISO,
      output: req.output ? JSON.stringify(req.output) : undefined,
      magicUrl: magicUrl || undefined,
      escalated: isEscalated,
      expirationTime: req.expiration_time || undefined,
    };
  });

  // Check if confirmation is required for this location type based on confirmation_flow
  const confirmationRequired = confirmationset?.confirmation_flow
    ? isPickup
      ? hasPickupFlow(confirmationset.confirmation_flow)
      : hasDeliveryFlow(confirmationset.confirmation_flow)
    : false;

  // Only create confirmation object if there are actual attempts OR confirmation is required
  const hasConfirmationData = attempts.length > 0 || confirmationRequired;

  // Get latest confirmation status - use raw database status
  const latestStatus = attempts[0]?.status || 'pending';

  // Get magic URL from any request that has it (prefer latest, but fall back to earlier attempts)
  // The magic URL should be consistent across attempts for the same confirmation flow
  const latestMagicUrl =
    latestRequest?.magic_url || relevantRequests.find((req: any) => req.magic_url)?.magic_url || null;

  return {
    name,
    address,
    region,
    contact,
    confirmation: hasConfirmationData
      ? {
          status: latestStatus,
          magicUrl: latestMagicUrl,
          attempts,
        }
      : null,
    confirmationset: confirmationset
      ? {
          id: confirmationset.id,
          confirmation_flow: confirmationset.confirmation_flow,
          pickup_workflow_id: confirmationset.pickup_workflow_id,
          delivery_workflow_id: confirmationset.delivery_workflow_id,
        }
      : null,
    relevantRequests,
  };
};
